import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FRAU_MEETS_BOERSE_CONFIG, isFrauMeetsBoerseHost } from '@/consts/frauMeetsBoerse';
import '@fontsource/hanken-grotesk/400.css';
import '@fontsource/hanken-grotesk/500.css';
import '@fontsource/hanken-grotesk/600.css';
import '@fontsource/hanken-grotesk/700.css';
import '@fontsource/hanken-grotesk/800.css';
import '@fontsource/instrument-serif/400.css';
import '@fontsource/instrument-serif/400-italic.css';

const config = FRAU_MEETS_BOERSE_CONFIG;

// Display-Schrift der Seite (Instrument Serif) — als Klassen-Konstante, damit
// der Tailwind-Scanner das Utility findet und es nur einmal definiert ist.
export const FMB_SERIF = '[font-family:var(--fmb-font-serif)]';

/** Pfad der Landingpage — auf den externen Domains '/', sonst die App-Route */
export function fmbHomePath(): string {
  return isFrauMeetsBoerseHost() ? '/' : config.routePath;
}

/**
 * Interner Link-Pfad relativ zur Landingpage: Auf den externen Domains
 * bleiben die Pfade clean ('/impressum'), auf der Hauptdomain werden sie
 * unter die Landingpage-Route gehängt ('/frau-meets-boerse/impressum'),
 * damit das Frau-meets-Börse-Design erhalten bleibt.
 */
export function fmbInternalPath(href: string): string {
  return isFrauMeetsBoerseHost() ? href : `${config.routePath}${href}`;
}

function buildThemeStyle(): CSSProperties {
  const c = config.colors;
  return {
    '--fmb-bg': c.bg,
    '--fmb-ink': c.ink,
    '--fmb-accent': c.accent,
    '--fmb-accent-dark': c.accentDark,
    '--fmb-accent-light': c.accentLight,
    '--fmb-muted': c.muted,
    '--fmb-faint': c.faint,
    '--fmb-label': c.label,
    '--fmb-badge-border': c.badgeBorder,
    '--fmb-border': c.border,
    '--fmb-border-warm': c.borderWarm,
    '--fmb-border-card': c.borderCard,
    '--fmb-card-divider': c.cardDivider,
    '--fmb-section-bg': c.sectionBg,
    '--fmb-dark-border': c.darkBorder,
    '--fmb-dark-text': c.darkText,
    '--fmb-dark-muted': c.darkMuted,
    '--fmb-card-text': c.cardText,
    '--fmb-stars': c.stars,
    '--fmb-cta-kicker': c.ctaKicker,
    '--fmb-cta-text': c.ctaText,
    '--fmb-cta-ps': c.ctaPs,
    '--fmb-cta-button-hover': c.ctaButtonHover,
    '--fmb-shadow-hero': c.shadowHero,
    '--fmb-shadow-cta': c.shadowCta,
    '--fmb-shadow-card': c.shadowCard,
    '--fmb-shadow-team': c.shadowTeam,
    '--fmb-font-serif': "'Instrument Serif', serif",
    fontFamily: "'Hanken Grotesk', sans-serif"
  } as CSSProperties;
}

function Wordmark({ small = false }: { small?: boolean }) {
  const logo = config.images.logo;
  if (logo.src !== null) {
    return <img src={logo.src} alt={logo.alt} className={small ? 'h-6 w-auto' : 'h-9 w-auto'} />;
  }

  return (
    <div className={`flex items-baseline gap-[2px] ${small ? 'text-[16px]' : 'text-[20px] tracking-[-0.01em]'}`}>
      <span className="font-normal">{config.wordmark.pre}</span>
      <span className="font-extrabold">{config.wordmark.bold}</span>
      <span className="font-normal">{config.wordmark.post}</span>
      {!small && <span className="ml-1 inline-block h-[6px] w-[6px] rounded-full bg-[var(--fmb-accent)]" />}
    </div>
  );
}

function Header({ showCta }: { showCta: boolean }) {
  return (
    <header className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-[clamp(20px,5vw,40px)] py-6">
      <Link to={fmbHomePath()} className="no-underline text-inherit">
        <Wordmark />
      </Link>
      {showCta && (
        <Link
          to={fmbInternalPath(config.videosPath)}
          className="cursor-pointer rounded-full bg-[var(--fmb-ink)] px-[22px] py-[11px] text-[14px] font-bold whitespace-nowrap text-[var(--fmb-bg)] no-underline transition-colors hover:bg-[var(--fmb-accent)]"
        >
          {config.headerCtaLabel}
        </Link>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--fmb-border)]">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-6 px-[clamp(20px,5vw,40px)] py-8">
        <Wordmark small />
        <div className="flex gap-6 text-[13px] text-[var(--fmb-faint)]">
          {config.footer.links.map((link) =>
            link.href.startsWith('http') ? (
              <a
                key={link.label}
                href={link.href}
                className="text-inherit no-underline transition-colors hover:text-[var(--fmb-accent)]"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={fmbInternalPath(link.href)}
                className="text-inherit no-underline transition-colors hover:text-[var(--fmb-accent)]"
              >
                {link.label}
              </Link>
            )
          )}
        </div>
        <div className="text-[13px] text-[var(--fmb-faint)]">{config.footer.copyright}</div>
      </div>
    </footer>
  );
}

interface FmbLayoutProps {
  title: string;
  /**
   * Header-CTA „Zu den gratis Umsetzungsvideos" anzeigen. Auf der Videoseite
   * selbst ausgeblendet — dort würde der Button auf die eigene Seite zeigen
   * und wäre der prominenteste Ausgang direkt über dem Geschenk-Kopfbereich.
   */
  showHeaderCta?: boolean;
  children: ReactNode;
}

/**
 * Gemeinsames Seitengerüst der Frau-meets-Börse-Seiten:
 * Theme-Variablen, Fonts, Header und Footer im Warm-Editorial-Design.
 */
export default function FmbLayout({ title, showHeaderCta = true, children }: FmbLayoutProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <div className="min-h-screen scroll-smooth bg-[var(--fmb-bg)] text-[var(--fmb-ink)]" style={buildThemeStyle()}>
      <Header showCta={showHeaderCta} />
      {children}
      <Footer />
    </div>
  );
}
